import type { ConfigAssignmentAssignmentTag } from '@arhs-fleetback/types/entities/tags';
import { createSelector } from 'reselect';

import type { MayBe } from 'modules/global/typing/common/common';
import { AssignmentOrigin, TAG_FEATURES_BY_MODE } from 'modules/tags/constants';
import { getFeaturesByDealerId } from 'selectors/dealerSelectors';
import { getProps, getSecondProps } from 'utils/selectors';

import type { TagsReducer } from '../reducer';
import type { StoredTag } from '../types';

type State = { tags: TagsReducer };

const getTagsState = (state: State): TagsReducer => state.tags;

const getDataState = createSelector(getTagsState, state => state.data);

export const getUIState = createSelector(getTagsState, state => state.ui);

const getTagsEntities = createSelector(getDataState, data => data.entities as Record<string, StoredTag>);

/**
 * Returns the stored tag for `id`. The store merges several projections by id, so pass the
 * projection you expect at the call site — e.g. `getTagById<BasicTag>(state, id)`. Defaults to the
 * `StoredTag` union.
 */
export const getTagById = createSelector(getTagsEntities, getProps<string>, (entities, id) => entities[id]) as unknown as
  <T = StoredTag>(state: State, id: string) => T;

/**
 * Returns the stored tags for `ids`. Pass the expected projection at the call site —
 * e.g. `getTagByIds<BasicTag>(state, ids)`. Defaults to the `StoredTag` union.
 */
export const getTagByIds = createSelector(getTagsEntities, getProps<string[]>, (entities, ids) =>
  ids.map(id => entities[id]).filter(Boolean)) as unknown as <T = StoredTag>(state: State, ids: string[]) => T[];

export const getGroupOrigin = createSelector(
  getTagById<MayBe<ConfigAssignmentAssignmentTag>>,
  tag => tag?.origin === AssignmentOrigin.GROUP
);

export const canDisplayTags = createSelector(
  getFeaturesByDealerId,
  getSecondProps<keyof typeof TAG_FEATURES_BY_MODE>,
  (dealerFeatures: string[], mode: keyof typeof TAG_FEATURES_BY_MODE) =>
    TAG_FEATURES_BY_MODE[mode].FEATURES.some(extension => dealerFeatures.includes(extension))
);
